import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Language Service - Manages language switching with Angular Signals
 *
 * Features:
 * - Reactive language state with signals
 * - LocalStorage persistence
 * - Browser language detection
 * - RTL language support detection
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private translocoService = inject(TranslocoService);

  readonly currentLang = signal<string>(this.translocoService.getActiveLang());

  // Computed: Check if current language is RTL
  readonly isRTL = computed(() => {
    const rtlLanguages = ['ar', 'he', 'fa', 'ur'];
    return rtlLanguages.includes(this.currentLang());
  });

  // Computed: Get language display name
  readonly currentLangName = computed(() => {
    const langNames: Record<string, string> = {
      en: 'English',
      fr: 'Français',
      de: 'Deutsch',
    };
    return langNames[this.currentLang()] || this.currentLang();
  });

  public get availableLangs(): string[] {
    return this.translocoService.config.availableLangs as string[];
  }

  constructor() {
    // Effect to update HTML lang attribute
    effect(() => {
      document.documentElement.lang = this.currentLang();
      document.documentElement.dir = this.isRTL() ? 'rtl' : 'ltr';
    });

    // Initialize language on service creation
    this.initLanguage();
  }

  /**
   * Initialize language from localStorage or browser preference
   */
  public initLanguage(): void {
    const storedLang = this.getStoredLanguage();
    const browserLang = this.getBrowserLanguage();
    const defaultLang = storedLang || browserLang || 'en';

    this.setLanguage(defaultLang);
  }

  /**
   * Set active language
   */
  public setLanguage(lang: string): void {
    if (!this.availableLangs.includes(lang as any)) {
      console.warn(`Language "${lang}" is not available. Falling back to "en".`);
      lang = 'en';
    }

    this.translocoService.setActiveLang(lang);
    this.currentLang.set(lang);
    this.storeLanguage(lang);
  }

  /**
   * Toggle between available languages
   */
  toggleLanguage(): void {
    const currentIndex = this.availableLangs.indexOf(this.currentLang() as any);
    const nextIndex = (currentIndex + 1) % this.availableLangs.length;
    this.setLanguage(this.availableLangs[nextIndex]);
  }

  /**
   * Get stored language preference from localStorage
   */
  public getStoredLanguage(): string | null {
    if (typeof localStorage !== 'undefined') {
      return localStorage.getItem('preferred-language');
    }
    return null;
  }

  /**
   * Store language preference in localStorage
   */
  private storeLanguage(lang: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('preferred-language', lang);
    }
  }

  /**
   * Get browser's preferred language
   */
  private getBrowserLanguage(): string | null {
    if (typeof navigator !== 'undefined') {
      const browserLang = navigator.language.split('-')[0];
      return this.availableLangs.includes(browserLang as any) ? browserLang : null;
    }
    return null;
  }
}
